import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { SalesPanel } from "./SalesPanel";

interface EventDay {
  id: string;
  businessDate: string;
  status: "OPEN" | "CLOSED";
  openingCashCents: number;
  countedCashCents: number | null;
  saleCount: number;
  salesTotalCents: number;
  cashSalesCents: number;
  cardSalesCents: number;
  expectedCashCents: number;
  cashDifferenceCents: number | null;
}

interface FairEvent {
  id: string;
  name: string;
  location: string | null;
  startDate: string;
  endDate: string;
  notes: string | null;
  status: "DRAFT" | "OPEN" | "CLOSED";
  days: EventDay[];
  summary: {
    completed: number;
    cancelled: number;
    units: number;
    totalCents: number;
    cashCents: number;
    cardCents: number;
    devices: number;
    bridge: {
      total: number;
      received: number;
      confirmed: number;
      errors: number;
      downloaded: boolean;
    };
  };
}

interface OperationsOverview {
  events: FairEvent[];
  activeDevices: number;
}

interface EventForm {
  name: string;
  location: string;
  startDate: string;
  endDate: string;
  notes: string;
}

const emptyEventForm: EventForm = {
  name: "",
  location: "",
  startDate: "",
  endDate: "",
  notes: "",
};

const money = new Intl.NumberFormat("ca-ES", {
  style: "currency",
  currency: "EUR",
});

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const data = (await response.json().catch(() => null)) as
    | T
    | { message?: string; error?: string }
    | null;
  if (!response.ok) {
    const error = data as { message?: string; error?: string } | null;
    throw new Error(error?.message ?? error?.error ?? "Operació no disponible.");
  }
  return data as T;
}

function parseMoneyToCents(value: string): number | null {
  const normalized = value.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

function today(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export function OperationsPanel() {
  const [overview, setOverview] = useState<OperationsOverview | null>(null);
  const [form, setForm] = useState<EventForm>(emptyEventForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [openingCash, setOpeningCash] = useState("0,00");
  const [businessDate, setBusinessDate] = useState(today());
  const [countedCash, setCountedCash] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await request<OperationsOverview>(
      "/api/v1/operations/overview",
    );
    setOverview(data);
  }, []);

  useEffect(() => {
    void load().catch((loadError: unknown) =>
      setError(
        loadError instanceof Error
          ? loadError.message
          : "No s'han pogut carregar les fires.",
      ),
    );
  }, [load]);

  const openEvent = overview?.events.find((event) => event.status === "OPEN");
  const openDay = openEvent?.days.find((day) => day.status === "OPEN");
  const closedDays = useMemo(
    () => openEvent?.days.filter((day) => day.status === "CLOSED") ?? [],
    [openEvent],
  );

  async function run(action: string, operation: () => Promise<unknown>) {
    setBusy(action);
    setError("");
    setNotice("");
    try {
      await operation();
      await load();
    } catch (operationError) {
      setError(
        operationError instanceof Error
          ? operationError.message
          : "L'operació ha fallat.",
      );
    } finally {
      setBusy("");
    }
  }

  async function saveEvent(event: FormEvent) {
    event.preventDefault();
    const action = editingId ? "edit-event" : "create-event";
    await run(action, async () => {
      await request(
        editingId
          ? `/api/v1/operations/events/${editingId}`
          : "/api/v1/operations/events",
        {
          method: editingId ? "PATCH" : "POST",
          body: JSON.stringify({
            name: form.name,
            location: form.location || null,
            startDate: form.startDate,
            endDate: form.endDate,
            notes: form.notes || null,
          }),
        },
      );
      setForm(emptyEventForm);
      setEditingId(null);
      setNotice(editingId ? "Fira actualitzada." : "Fira creada en esborrany.");
    });
  }

  function editEvent(event: FairEvent) {
    setEditingId(event.id);
    setForm({
      name: event.name,
      location: event.location ?? "",
      startDate: event.startDate,
      endDate: event.endDate,
      notes: event.notes ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function openDayForEvent(eventId: string) {
    const cents = parseMoneyToCents(openingCash);
    if (cents === null) {
      setError("Introdueix un fons inicial vàlid.");
      return;
    }
    await run("open-day", async () => {
      await request(`/api/v1/operations/events/${eventId}/days`, {
        method: "POST",
        body: JSON.stringify({
          businessDate,
          openingCashCents: cents,
        }),
      });
      setNotice("Jornada i caixa compartida obertes.");
    });
  }

  async function closeCurrentDay(dayId: string) {
    const cents = parseMoneyToCents(countedCash);
    if (cents === null) {
      setError("Introdueix l'efectiu comptat.");
      return;
    }
    if (!window.confirm("Vols tancar la jornada amb aquest efectiu comptat?")) {
      return;
    }
    await run("close-day", async () => {
      await request(`/api/v1/operations/days/${dayId}/close`, {
        method: "POST",
        body: JSON.stringify({ countedCashCents: cents }),
      });
      setCountedCash("");
      setNotice("Jornada tancada correctament.");
    });
  }

  return (
    <div className="operations-stack">
      <section className="operations-summary">
        <article>
          <span>Fira oberta</span>
          <strong>{openEvent?.name ?? "Cap"}</strong>
        </article>
        <article>
          <span>Jornada</span>
          <strong>{openDay ? "Oberta" : "Tancada"}</strong>
        </article>
        <article>
          <span>Dispositius actius</span>
          <strong>{overview?.activeDevices ?? 0}</strong>
        </article>
      </section>

      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="alert success">{notice}</p>}

      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">CONFIGURACIÓ</p>
            <h2>{editingId ? "Edita la fira" : "Crea una fira"}</h2>
          </div>
          {editingId && (
            <button
              className="secondary"
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyEventForm);
              }}
            >
              Cancel·lar edició
            </button>
          )}
        </div>

        <form className="event-form" onSubmit={(event) => void saveEvent(event)}>
          <label>
            Nom de la fira
            <input
              value={form.name}
              required
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
            />
          </label>
          <label>
            Ubicació
            <input
              value={form.location}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  location: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Data inicial
            <input
              type="date"
              value={form.startDate}
              required
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  startDate: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Data final
            <input
              type="date"
              value={form.endDate}
              required
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  endDate: event.target.value,
                }))
              }
            />
          </label>
          <label className="event-notes">
            Notes
            <textarea
              value={form.notes}
              rows={3}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </label>
          <button className="primary" disabled={Boolean(busy)}>
            {busy === "create-event" || busy === "edit-event"
              ? "Desant…"
              : editingId
                ? "Desar canvis"
                : "Crear esborrany"}
          </button>
        </form>
      </section>

      {openEvent && (
        <section className="panel cash-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">CAIXA COMPARTIDA</p>
              <h2>{openEvent.name}</h2>
            </div>
            <span className="status-pill status-open">Fira oberta</span>
          </div>

          {openDay ? (
            <>
              <div className="fair-total-highlight">
                <div>
                  <span>Total acumulat de la fira</span>
                  <small>{openEvent.summary.completed} vendes vàlides</small>
                </div>
                <strong>{money.format(openEvent.summary.totalCents / 100)}</strong>
              </div>
              <div className="cash-metrics">
                <article>
                  <span>Fons inicial</span>
                  <strong>{money.format(openDay.openingCashCents / 100)}</strong>
                </article>
                <article>
                  <span>Vendes</span>
                  <strong>{openDay.saleCount}</strong>
                  <small>{money.format(openDay.salesTotalCents / 100)}</small>
                </article>
                <article>
                  <span>Efectiu venut</span>
                  <strong>{money.format(openDay.cashSalesCents / 100)}</strong>
                </article>
                <article>
                  <span>Targeta venuda</span>
                  <strong>{money.format(openDay.cardSalesCents / 100)}</strong>
                </article>
                <article>
                  <span>Efectiu esperat</span>
                  <strong>{money.format(openDay.expectedCashCents / 100)}</strong>
                </article>
              </div>
              <div className="close-day-row">
                <label>
                  Efectiu comptat al tancament
                  <input
                    inputMode="decimal"
                    placeholder="0,00"
                    value={countedCash}
                    onChange={(event) => setCountedCash(event.target.value)}
                  />
                </label>
                <button
                  className="secondary"
                  type="button"
                  disabled={Boolean(busy)}
                  onClick={() => void closeCurrentDay(openDay.id)}
                >
                  {busy === "close-day" ? "Tancant…" : "Tancar jornada"}
                </button>
              </div>
            </>
          ) : (
            <div className="open-day-row">
              <label>
                Data de la jornada
                <input
                  type="date"
                  min={openEvent.startDate}
                  max={openEvent.endDate}
                  value={businessDate}
                  onChange={(event) => setBusinessDate(event.target.value)}
                />
              </label>
              <label>
                Fons inicial compartit
                <input
                  inputMode="decimal"
                  value={openingCash}
                  onChange={(event) => setOpeningCash(event.target.value)}
                />
              </label>
              <button
                className="primary"
                type="button"
                disabled={Boolean(busy)}
                onClick={() => void openDayForEvent(openEvent.id)}
              >
                {busy === "open-day" ? "Obrint…" : "Obrir jornada"}
              </button>
            </div>
          )}

          {closedDays.length > 0 && (
            <div className="day-history">
              <h3>Jornades tancades</h3>
              {closedDays.map((day) => (
                <div key={day.id}>
                  <span>{day.businessDate}</span>
                  <span>{day.saleCount} vendes</span>
                  <strong>
                    Diferència:{" "}
                    {money.format((day.cashDifferenceCents ?? 0) / 100)}
                  </strong>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">HISTORIAL</p>
            <h2>Fires</h2>
          </div>
          <span className="badge">{overview?.events.length ?? 0} fires</span>
        </div>
        <div className="event-list">
          {overview?.events.map((event) => (
            <article className="event-card" key={event.id}>
              <div>
                <span className={`status-pill status-${event.status.toLowerCase()}`}>
                  {event.status === "DRAFT"
                    ? "Esborrany"
                    : event.status === "OPEN"
                      ? "Oberta"
                      : "Tancada"}
                </span>
                <h3>{event.name}</h3>
                <p>
                  {event.location ?? "Sense ubicació"} · {event.startDate} —{" "}
                  {event.endDate}
                </p>
                {event.status === "CLOSED" && (
                  <div className="event-history-summary">
                    <span><b>{event.summary.completed}</b> tickets</span>
                    <span><b>{event.summary.units}</b> unitats</span>
                    <span><b>{money.format(event.summary.totalCents / 100)}</b> total</span>
                    <span><b>{money.format(event.summary.cashCents / 100)}</b> efectiu</span>
                    <span><b>{money.format(event.summary.cardCents / 100)}</b> targeta</span>
                    <span><b>{event.summary.devices}</b> terminals</span>
                    <span className={event.summary.bridge.downloaded ? "bridge-complete" : "bridge-pending"}>
                      Bridge: {event.summary.bridge.total === 0
                        ? "sense tickets"
                        : `${event.summary.bridge.received}/${event.summary.bridge.total} descarregats`}
                    </span>
                  </div>
                )}
              </div>
              <div className="event-actions">
                {event.status === "CLOSED" && (
                  <button
                    className="primary"
                    type="button"
                    onClick={() => setSelectedHistoryId((current) => current === event.id ? null : event.id)}
                  >
                    {selectedHistoryId === event.id ? "Amagar historial" : "Veure tickets"}
                  </button>
                )}
                {event.status === "DRAFT" && (
                  <>
                    <button
                      className="secondary"
                      type="button"
                      onClick={() => editEvent(event)}
                    >
                      Editar
                    </button>
                    <button
                      className="primary"
                      type="button"
                      disabled={Boolean(openEvent) || Boolean(busy)}
                      onClick={() =>
                        void run("open-event", async () => {
                          await request(
                            `/api/v1/operations/events/${event.id}/open`,
                            { method: "POST" },
                          );
                          setBusinessDate(event.startDate);
                          setNotice("Fira oberta.");
                        })
                      }
                    >
                      Obrir fira
                    </button>
                  </>
                )}
                {event.status === "OPEN" && (
                  <>
                    {event.days.length === 0 && (
                      <button
                        className="secondary"
                        type="button"
                        onClick={() => editEvent(event)}
                      >
                        Editar
                      </button>
                    )}
                    <button
                      className="secondary"
                      type="button"
                      disabled={Boolean(openDay) || Boolean(busy)}
                      onClick={() => {
                        if (
                          window.confirm("Vols tancar definitivament la fira?")
                        ) {
                          void run("close-event", async () => {
                            await request(
                              `/api/v1/operations/events/${event.id}/close`,
                              { method: "POST" },
                            );
                            setNotice("Fira tancada.");
                          });
                        }
                      }}
                    >
                      Tancar fira
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
          {overview && overview.events.length === 0 && (
            <p className="empty-state">Encara no hi ha cap fira creada.</p>
          )}
        </div>
      </section>

      {selectedHistoryId && (() => {
        const selectedEvent = overview?.events.find((event) => event.id === selectedHistoryId);
        return selectedEvent ? (
          <SalesPanel
            eventId={selectedEvent.id}
            title={`Historial · ${selectedEvent.name}`}
            readOnly
          />
        ) : null;
      })()}
    </div>
  );
}
